import { useEffect } from 'react'

import { useFormContext, useWatch } from 'react-hook-form'

import { AddressSearchInput } from '~/components/component-library'
import { ExternalAccountSelectInput } from '~/components/inputs/custom/ExternalAccountSelectInput'
import { MultiRecipientFormValues } from '~/components/smart-accounts/schemas'
import { useCanOfframpOrOnramp } from '~/components/smart-accounts/useCanOfframpOrOnramp'
import { SupportedVmTypes } from '~/constants/chains'
import { usePayeeLookupByAddress } from '~/hooks/compliance/usePayeeLookup'
import { IAddress } from '~/types'

export const RecipientInput = ({
  isOfframpVariant = false,
  lockRecipient = false,
  sender,
  shouldAutoFocus = true,
  onSelect,
  defaultRecipient,
}: {
  isOfframpVariant?: boolean
  lockRecipient?: boolean
  sender: IAddress
  shouldAutoFocus?: boolean
  onSelect: (receiver: IAddress) => void
  defaultRecipient?: IAddress
}) => {
  const { control, setValue, setError } =
    useFormContext<MultiRecipientFormValues>()

  const receiver = useWatch({ control, name: 'recipients.0.receiver' })

  const { payee, isFetching: isLoadingPayee } = usePayeeLookupByAddress(
    receiver?.address,
  )

  // Same gate as the Offramp/Onramp dialog entry points
  const includeBankAccounts = useCanOfframpOrOnramp()

  useEffect(() => {
    if (defaultRecipient) {
      setValue('recipients.0.receiver.address', defaultRecipient)
      onSelect(defaultRecipient)
    }
  }, [defaultRecipient, setValue, onSelect])

  useEffect(() => {
    if (!isLoadingPayee) {
      if (payee) {
        setValue('recipients.0.receiver.compliantPaymentRelationId', payee.id)
      } else {
        setValue('recipients.0.receiver.compliantPaymentRelationId', undefined)
      }
    }
  }, [payee, setValue, setError, isLoadingPayee])

  return isOfframpVariant ? (
    <ExternalAccountSelectInput
      label="To"
      selectedOption={receiver?.address}
      isDisabled={lockRecipient}
      setValue={(value) => {
        setValue('recipients.0.receiver.address', value)
        onSelect(value)
      }}
      emptyText={'Select bank account'}
    />
  ) : (
    <AddressSearchInput
      label="To"
      control={control}
      disabled={lockRecipient}
      inputName="recipients.0.receiver.address"
      setValue={setValue}
      vmTypes={SupportedVmTypes}
      includeBankAccounts={includeBankAccounts}
      autoFocus={!!sender && shouldAutoFocus}
      onSelect={onSelect}
      footer={
        payee?.payeeInvitedEmail
          ? `We'll send a payment notification to ${payee.payeeInvitedEmail}`
          : undefined
      }
    />
  )
}
